'use client';

import {useEffect, useRef, useCallback} from 'react';
import {useTranslations, useLocale} from 'next-intl';
import {usePathname} from 'next/navigation';
import Button from '../ui/Button';
import LanguageSwitcher from './LanguageSwitcher';
import SearchBar from './SearchBar';

const navKeys = ['home', 'about', 'news', 'programs', 'aids', 'services', 'dar_ourabi', 'structures', 'projects', 'accomplished', 'testimonials', 'videos', 'partners', 'contact'] as const;

const navPaths: Record<string, string> = {
  home: '',
  about: '/a-propos',
  news: '/actualites',
  programs: '/programmes',
  aids: '/les-aides',
  services: '/services-actifs',
  dar_ourabi: '/dar-ourabi',
  structures: '/structures',
  projects: '/projets',
  accomplished: '/projets-accomplis',
  testimonials: '/temoignages',
  videos: '/videos',
  partners: '/partenaires',
  contact: '/contact',
};

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export default function MobileMenu({open, onClose}: MobileMenuProps) {
  const t = useTranslations('nav');
  const locale = useLocale();
  const pathname = usePathname();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Focus trap (#49)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = panelRef.current.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, [open, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div ref={panelRef} className={`fixed inset-y-0 end-0 w-80 max-w-full bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <span className="text-primary font-bold text-lg">UTSS</span>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface" aria-label="Fermer">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <SearchBar />
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1" aria-label="Navigation mobile">
          {navKeys.map((key) => {
            const href = `/${locale}${navPaths[key]}`;
            const isActive = pathname === href || (key !== 'home' && pathname.startsWith(href));
            return (
              <a
                key={key}
                href={href}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  isActive ? 'text-primary bg-primary/5' : 'text-foreground/70 hover:text-primary hover:bg-surface'
                }`}
                onClick={onClose}
              >
                {t(key)}
              </a>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <LanguageSwitcher />
          <Button href={`/${locale}/don`} variant="accent" size="md" className="w-full">
            {t('donate')}
          </Button>
        </div>
      </div>
    </div>
  );
}
